import { getErrorDisplayMarkup } from "@context-providers/error-boundary/error-boundary-utils";
import { hasXgSeatPermission } from "@utils/permission-utils";
import { getPrettyName } from "@utils/user-utils";
import { getTeamXgSeatRoleMessage } from "@utils/xg-seat-utils";
import { uniqBy } from "es-toolkit";
/** What the user is told when somebody selected cannot be given the selected project role */
export const PROJECT_XG_SEAT_REQUIRED_MESSAGE = "Only members with a Sphere XG seat can be Project Admins or Editors.";

/** What the user is told when somebody selected cannot be given the selected group role */
export const GROUP_XG_SEAT_REQUIRED_MESSAGE = "Only members with a Sphere XG seat can be Group Managers or Project Managers.";

/** What the seat card in the group invite says the Sphere XG seat is required for */
export const GROUP_XG_SEAT_DESCRIPTION = "Required for Group and Project Managers";

/** What a user that could give seats is told when somebody selected cannot get the role */
export const NO_XG_SEATS_LEFT_MESSAGE = "There are no Sphere XG seats left. Remove the people without a seat to continue.";

/** What the user is told next to a member that cannot be selected for the role */
export const NO_XG_SEAT_MESSAGE = "No Sphere XG seat";

/**
 * Why a selected entry cannot get a role that needs a Sphere XG seat:
 * - `team`: teams cannot be checked for seats, so they are not allowed at all
 * - `noSeat`: somebody without a seat that the current user cannot give one: a member of the
 *   workspace, pending or not, or an email of somebody outside of it, who are treated the same
 */

/**
 * Returns what the user is told about the selected entries that cannot get the role. A team is told
 * about the same way as wherever a role is changed, see getTeamXgSeatRoleMessage.
 */
export function getBlockedMessage({
  reasons,
  isOutOfSeats,
  seatRequiredMessage,
  viewerRole
}) {
  const noSeatMessage = isOutOfSeats ? NO_XG_SEATS_LEFT_MESSAGE : seatRequiredMessage;
  if (!reasons.has("team")) {
    return noSeatMessage;
  }
  const teamMessage = getTeamXgSeatRoleMessage(viewerRole);
  return reasons.has("noSeat") ? `${teamMessage} ${noSeatMessage}` : teamMessage;
}
/**
 * Returns the message below the members field: what is typed comes first, then whatever keeps the
 * selection from being invited, and hints that do not, e.g. about duplicates, come last
 */
export function getInviteHelperText({
  inputHelperText,
  selectionHelperText,
  xgSeatsHelperText
}) {
  if (inputHelperText) {
    return inputHelperText;
  }

  // Without typed text, the message of the autocomplete is about the selection, e.g. too many
  // members, which blocks the invite on its own
  if (selectionHelperText?.type === "error") {
    return selectionHelperText;
  }
  return xgSeatsHelperText ?? selectionHelperText;
}

/** Whether members need a Sphere XG seat for a project role on a Scan plan, which is any but viewer */
export function doesProjectRoleNeedXgSeat(role) {
  return !!role && role !== "RESOURCE_VIEWER";
}

/**
 * Whether a member lacks the Sphere XG seat, which they hold as the `activeSeat` permission.
 *
 * Only the assigned seat counts here, unlike getHeldSeats: this is checked on a Scan plan, where
 * the seats are the `ACTIVE_SEAT` constraint, so no member holds one for being an active user.
 */
export function isMissingXgSeat(member) {
  return !hasXgSeatPermission({
    member
  });
}

/** Returns the members by their identity and their lower-cased email, to look up selected values */
export function getMembersByKey(members) {
  return new Map(members.flatMap(member => [[member.identity, member], [member.email.toLowerCase(), member]]));
}

/**
 * Returns the keys a team can be selected with: its ID when picked from the options, and its name
 * when typed
 */
export function getTeamKeys(teams) {
  return new Set(teams.flatMap(({
    id,
    name
  }) => [id, name]));
}

/** The selected people that need a Sphere XG seat for the role they are invited with */

/** Somebody selected that could not be invited, e.g. since they could not be given their seat */

/**
 * Returns which of the selected entries lack the Sphere XG seat that the role needs. Typed
 * emails count as the member they belong to. An entry matching no member is an email of somebody
 * outside the workspace, who lacks a seat like a member without one, when all the members are
 * known. Otherwise it is left to the backend.
 */
export function getSelectionXgSeats({
  selectedValues,
  membersByKey,
  teamKeys,
  areAllMembersKnown,
  canGiveSeats
}) {
  const blockedValues = new Map();
  const valuesToSeat = new Set();
  const membersToSeat = new Map();
  const emailsToSeat = new Map();
  for (const value of selectedValues) {
    if (teamKeys.has(value)) {
      blockedValues.set(value, "team");
      continue;
    }
    const member = membersByKey.get(value) ?? membersByKey.get(value.toLowerCase());
    if (member ? !isMissingXgSeat(member) : !areAllMembersKnown) {
      continue;
    }
    if (!canGiveSeats) {
      blockedValues.set(value, "noSeat");
      continue;
    }
    valuesToSeat.add(value);
    // Somebody can be selected twice, by their name and by their typed email, or by an email
    // typed in another case
    if (member) {
      membersToSeat.set(member.identity, member);
    } else if (!emailsToSeat.has(value.toLowerCase())) {
      emailsToSeat.set(value.toLowerCase(), value);
    }
  }
  return {
    blockedValues,
    valuesToSeat,
    membersToSeat: [...membersToSeat.values()],
    emailsToSeat: [...emailsToSeat.values()]
  };
}

/**
 * Returns the chip color of a selected value: error when it cannot get the role, and none when it
 * can. The people that are given a seat look like everybody else, since the seat card tells how
 * many seats they take, unless the free seats are too few for them: then they are all errors, as
 * none of them can be invited.
 */
export function getXgSeatChipColor(value, {
  blockedValues,
  valuesToSeat
}, hasEnoughSeats) {
  if (blockedValues.has(value)) {
    return "error";
  }
  return valuesToSeat.has(value) && !hasEnoughSeats ? "error" : undefined;
}

/** The members and teams of the workspace, to tell the subject behind a selected value */

/**
 * Returns the subject a selected value stands for, as listed when it could not be invited: a
 * member picked by identity or typed as their email, a team picked by ID or typed as its name (see
 * getTeamKeys), or else the value itself, e.g. the email of somebody new. So the same subject
 * selected in two forms has one key.
 *
 * @param value The selected value
 * @param subjects The members and teams of the workspace
 */
export function getInvitedSubject(value, {
  members,
  teams
}) {
  const member = members.find(({
    identity,
    email
  }) => identity === value || email.toLowerCase() === value.toLowerCase());
  if (member) {
    return {
      key: member.identity,
      label: getPrettyName(member)
    };
  }
  const team = teams.find(({
    id,
    name
  }) => id === value || name === value);
  if (team) {
    return {
      key: team.id,
      label: team.name
    };
  }
  return {
    key: value.toLowerCase(),
    label: value
  };
}

/**
 * Returns the people that an invite, e.g. to a project or a group, was refused for, with why
 *
 * @param response The response of the invite
 * @param subjects The members and teams of the workspace
 */
export function getInviteResponseFailures({
  errorData
}, subjects) {
  const details = errorData?.details;
  return [...(details?.failedInvitations ?? []), ...(details?.failedPermissions ?? [])].map(({
    identity,
    message
  }) => ({
    ...getInvitedSubject(identity, subjects),
    // The backend identifies them, so its identity is kept to find them in the store
    key: identity,
    reason: message
  }));
}

/**
 * Whether the invite reached anybody, i.e. not every invited person is among those the invite
 * response refused. Both are compared as people, so somebody selected by name and by their typed
 * email counts once.
 *
 * @param values The selected values that were invited
 * @param responseFailures The people the response refused, see getInviteResponseFailures
 * @param subjects The members and teams of the workspace
 */
export function isAnyoneInvited(values, responseFailures, subjects) {
  const refusedKeys = new Set(responseFailures.map(({
    key
  }) => getInvitedSubject(key, subjects).key));
  return values.some(value => !refusedKeys.has(getInvitedSubject(value, subjects).key));
}

/**
 * Returns everybody that an invite request failed for as a whole, each of them once
 *
 * @param values The selected values that were invited
 * @param subjects The members and teams of the workspace
 * @param error Why the request failed
 */
export function getInviteRequestFailures(values, subjects, error) {
  const reason = getErrorDisplayMarkup(error);
  // Somebody can be selected twice, by their name and by their typed email
  return uniqBy(values.map(value => ({
    ...getInvitedSubject(value, subjects),
    reason
  })), ({
    key
  }) => key);
}